import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import './header.scss'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import Link from '../LanguageSwitcher/LocaleLink'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import LogoMark from '../svg/LogoMark';
import { splitLocale } from '../../config/i18nRoutes';
import allServices, { SERVICE_CATEGORIES } from '../../data/services';
import { localizeServices } from '../../data/localizeService';
import allIndustries from '../../data/industries';
import { localizeIndustries } from '../../data/localizeIndustry';
import CloseBtn from '../../images/close-btn.svg'

const categoryLabel = (translations, category) =>
  translations[`svcCat${category.id.charAt(0).toUpperCase()}${category.id.slice(1)}`] || category.label

const Chevron = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' aria-hidden='true' focusable='false'>
    <path d='M2.5 4.5 6 8l3.5-3.5' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

/**
 * A header menu with a panel: opens on hover, click or keyboard; closes on
 * Escape, an outside click, leaving it with the mouse, or navigating away.
 * The panel is positioned against the whole bar (see .header-menu).
 */
const NavDropdown = ({ id, label, active, pathName, reduced, onOpenChange, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => { setOpen(false); }, [pathName]);
  useEffect(() => { onOpenChange(id, open); }, [id, open, onOpenChange]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  // A short grace period so moving the pointer from the button down into the
  // panel does not close it.
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
      ref.current?.querySelector('button')?.focus();
    }
  };

  return (
    <div className='header-dropdown' ref={ref} onMouseEnter={show} onMouseLeave={scheduleClose} onKeyDown={onKeyDown}>
      <button
        type='button'
        className={`header-link${open || active ? ' header-link-active' : ''}`}
        aria-expanded={open}
        aria-controls={id}
        // Hover has already opened it for a mouse, so a click must not
        // close it again; the keyboard (detail 0) toggles.
        onClick={(event) => (event.detail === 0 ? setOpen((value) => !value) : show())}
      >
        {label}
        <span className={`header-chevron${open ? ' header-chevron-open' : ''}`}><Chevron /></span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={id}
            className='header-menu'
            // Never start hidden unless the fade can actually play
            // (see motion/useEntryMotion for why).
            initial={reduced || document.visibilityState !== 'visible' ? false : { opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: 'top center' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const location = useLocation();
  const pathName = splitLocale(location.pathname).base;
  const { translations } = useContext(LanguageContext);

  const [showBurgerMenu, setShowBurgerMenu] = useState(false);

  // Header gets out of the way when reading down the page and comes straight
  // back on any upward scroll.
  const reduced = useReducedMotion();
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);

  // Which header menus are open — the bar stays visible while one is.
  const [openMenus, setOpenMenus] = useState({});
  const onMenuOpenChange = useCallback((id, open) => {
    setOpenMenus((prev) => (prev[id] === open ? prev : { ...prev, [id]: open }));
  }, []);
  const anyMenuOpen = Object.values(openMenus).some(Boolean);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    if (showBurgerMenu || reduced || anyMenuOpen) {
      setHidden(false);
      return;
    }
    setHidden(latest > previous && latest > 260);
  });

  const services = localizeServices(allServices, translations);
  const industries = localizeIndustries(allIndustries, translations);

  const navigate = useNavigate();

  const handleOpenMenu = () => {
    setShowBurgerMenu(true)
  }

  const handleCloseMenu = () => {
    setShowBurgerMenu(false)

  }

  const handleNavigateServicesBurgerMenu = () => {
    navigate('/services')
    setShowBurgerMenu(false)
  }

  const handleNavigateIndustriesBurgerMenu = () => {
    navigate('/industries')
    setShowBurgerMenu(false)
  }

  const handleNavigateProjectsBurgerMenu = () => {
    navigate('/projects')
    setShowBurgerMenu(false)

  }

  const handleNavigateBlogBurgerMenu = () => {
    navigate('/articles')
    setShowBurgerMenu(false)

  }

  const handleNavigateEstimateBurgerMenu = () => {
    navigate('/estimate')
    setShowBurgerMenu(false)
  }

  const handleNavigateProcessBurgerMenu = () => {
    navigate('/process')
    setShowBurgerMenu(false)
  }

  const handleNavigateAboutUsBurgerMenu = () => {
    navigate('/about-us')
    setShowBurgerMenu(false)
  }

  const handleNavigateContactPageBurgerMenu = () => {
    navigate('/contact-us')
    setShowBurgerMenu(false)
  }

  const linkClass = ({ isActive }) => `header-link${isActive ? ' header-link-active' : ''}`

  return (
    <div className='header-component-container'>

      <AnimatePresence>
      {showBurgerMenu &&
      <motion.div
        className='burger-menu'
        onClick={handleCloseMenu}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
      <motion.div
        className='burger-menu-content'
        onClick={(e) => e.stopPropagation()}
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -16, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >

        <div className='burger-menu-top'>
          <Link to='/' className='burger-menu-brand' aria-label='MLMind — home' onClick={handleCloseMenu}>
            <LogoMark size={32} />
          </Link>
          <button className='burger-close-btn' onClick={handleCloseMenu}>
            <img src={CloseBtn} alt='close menu' width={20} />
          </button>
        </div>

        <div className='burger-menu-header-right'>
          {[
            { label: translations.navServices, fn: handleNavigateServicesBurgerMenu },
            { label: translations.navIndustries || 'Industries', fn: handleNavigateIndustriesBurgerMenu },
            { label: translations.projects, fn: handleNavigateProjectsBurgerMenu },
            { label: translations.Blog, fn: handleNavigateBlogBurgerMenu },
            { label: translations.procNavLabel, fn: handleNavigateProcessBurgerMenu },
            { label: translations.estNavLabel, fn: handleNavigateEstimateBurgerMenu },
            { label: translations.about, fn: handleNavigateAboutUsBurgerMenu },
          ].map((item, i) => (
            <motion.p
              key={item.label}
              onClick={item.fn}
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12 + i * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className='burger-item-num'>{String(i + 1).padStart(2, '0')}</span> {item.label}
            </motion.p>
          ))}
        </div>

        <motion.div
          className='burger-menu-bottom'
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <button onClick={handleNavigateContactPageBurgerMenu} className='header-connect-btn burger-contact-btn'>{translations.contact}</button>
          <a
            className='burger-menu-social'
            href='https://www.facebook.com/profile.php?id=61570609156509'
            target='_blank'
            rel='noreferrer'
          >
            Facebook ↗
          </a>
        </motion.div>

      </motion.div>
      </motion.div>
      }
      </AnimatePresence>

      <motion.header
        className='header'
        animate={{ y: hidden ? '-120%' : '0%' }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <nav className='header-bar' aria-label='Main'>
          <Link to='/' className='header-logo' aria-label='MLMind — home'>
            <LogoMark size={34} tone='light' />
          </Link>

          <div className='header-links'>
            <NavDropdown
              id='header-services-menu'
              label={translations.navServices}
              active={pathName.startsWith('/services')}
              pathName={pathName}
              reduced={reduced}
              onOpenChange={onMenuOpenChange}
            >
              <div className='header-menu-grid'>
                {SERVICE_CATEGORIES.map((category) => (
                  <div key={category.id} className='header-menu-col'>
                    <p className='header-menu-title'>{categoryLabel(translations, category)}</p>
                    <ul>
                      {services
                        .filter((service) => service.category === category.id)
                        .map((service) => (
                          <li key={service.slug}>
                            <Link to={`/services/${service.slug}`} className='header-menu-link'>
                              {service.name}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
              <Link to='/services' className='header-menu-all'>
                {translations.navAllServices || 'All services'} <span aria-hidden='true'>→</span>
              </Link>
            </NavDropdown>

            <NavDropdown
              id='header-industries-menu'
              label={translations.navIndustries || 'Industries'}
              active={pathName.startsWith('/industries')}
              pathName={pathName}
              reduced={reduced}
              onOpenChange={onMenuOpenChange}
            >
              <ul className='header-menu-cards'>
                {industries.map((industry) => (
                  <li key={industry.slug}>
                    <Link to={`/industries/${industry.slug}`} className='header-menu-card'>
                      <span className='header-menu-card-title'>{industry.name}</span>
                      <span className='header-menu-card-text'>{industry.summary}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to='/industries' className='header-menu-all'>
                {translations.navAllIndustries || 'All industries'} <span aria-hidden='true'>→</span>
              </Link>
            </NavDropdown>

            <NavLink to='/projects' className={linkClass}>{translations.projects}</NavLink>
            <NavLink to='/process' className={linkClass}>{translations.procNavLabel}</NavLink>
            <NavLink to='/about-us' className={linkClass}>{translations.about}</NavLink>
            <NavLink to='/articles' className={linkClass}>{translations.Blog}</NavLink>
          </div>

          <div className='header-actions'>
            <LanguageSwitcher />
            <Link to='/contact-us' className='header-cta'>{translations.contact}</Link>
            <button type='button' className='header-burger-btn' aria-label={translations.uiOpenMenu} onClick={handleOpenMenu}>
              <svg width='22' height='22' viewBox='0 0 22 22' aria-hidden='true' focusable='false'>
                <path d='M3 6h16M3 11h16M3 16h16' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
              </svg>
            </button>
          </div>
        </nav>
      </motion.header>
    </div>
  )
}

export default Header
